import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import Modal, { ModalBody, ModalFooter } from './Modal';
import Button from './Button';
import Input from './Input';

type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function safeJsonify(value: unknown): JsonValue {
  try {
    return JSON.parse(JSON.stringify(value)) as JsonValue;
  } catch {
    return String(value) as unknown as JsonValue;
  }
}

function pathJoin(parent: string, key: string, isArrayIndex: boolean) {
  if (!parent) return isArrayIndex ? `[${key}]` : key;
  return isArrayIndex ? `${parent}[${key}]` : `${parent}.${key}`;
}

function getTypeLabel(v: JsonValue) {
  if (v === null) return 'null';
  if (Array.isArray(v)) return `array(${v.length})`;
  if (typeof v === 'object') return `object(${Object.keys(v).length})`;
  return typeof v;
}

function truncate(s: string, max = 120) {
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function valueToSearchString(v: JsonValue): string {
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // Avoid stringifying huge subtrees for search; leaf nodes are handled separately.
  return '';
}

type FlatEntry = {
  path: string;
  key: string;
  type: string;
  preview: string;
  value: JsonValue;
};

function flattenJson(value: JsonValue): FlatEntry[] {
  const out: FlatEntry[] = [];

  const walk = (v: JsonValue, path: string, key: string) => {
    const type = getTypeLabel(v);

    if (Array.isArray(v)) {
      out.push({ path, key, type, preview: '', value: v });
      v.forEach((child, i) => {
        walk(child, pathJoin(path, String(i), true), String(i));
      });
      return;
    }

    if (v && typeof v === 'object') {
      out.push({ path, key, type, preview: '', value: v });
      Object.entries(v).forEach(([k, child]) => {
        walk(child, pathJoin(path, k, false), k);
      });
      return;
    }

    const leafText = valueToSearchString(v);
    out.push({
      path,
      key,
      type,
      preview: typeof v === 'string' ? truncate(v, 160) : leafText,
      value: v,
    });
  };

  walk(value, '', '(root)');
  return out;
}

function highlight(text: string, q: string) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const mid = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <span>
      {before}
      <mark className="bg-yellow-200/70 dark:bg-yellow-500/30 rounded px-0.5">{mid}</mark>
      {after}
    </span>
  );
}

function jsonParents(path: string): string[] {
  // Build ancestor paths for expansion (rough but effective).
  if (!path) return [''];
  const parents: string[] = [''];
  let cur = '';
  // Tokenize by '.' and '[n]' segments
  const re = /(\[[0-9]+\])|([^.[]+)/g;
  const tokens = path.match(re) || [];
  for (const t of tokens) {
    if (t.startsWith('[')) {
      cur = `${cur}${t}`;
    } else {
      cur = cur ? `${cur}.${t}` : t;
    }
    parents.push(cur);
  }
  return parents;
}

function JsonTreeNode({
  name,
  value,
  path,
  depth,
  expanded,
  setExpanded,
  q,
  selectedPath,
  onSelectPath,
  nodeRefs,
  onViewString,
}: {
  name: string;
  value: JsonValue;
  path: string;
  depth: number;
  expanded: Set<string>;
  setExpanded: (next: Set<string>) => void;
  q: string;
  selectedPath: string | null;
  onSelectPath: (p: string) => void;
  nodeRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
  onViewString: (title: string, value: string) => void;
}) {
  const isArray = Array.isArray(value);
  const isObj = !isArray && isPlainObject(value);
  const isContainer = isArray || isObj;
  const isOpen = expanded.has(path);
  const typeLabel = getTypeLabel(value);

  const indentStyle = { paddingLeft: `${depth * 14}px` };
  const isSelected = selectedPath === path;

  const toggle = () => {
    const next = new Set(expanded);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setExpanded(next);
  };

  const copyPath = async () => {
    try {
      await navigator.clipboard.writeText(path || '(root)');
    } catch {
      // ignore
    }
  };

  const copyValue = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    } catch {
      // ignore
    }
  };

  const leaf =
    value === null ? (
      <span className="text-slate-500 dark:text-slate-400">null</span>
    ) : typeof value === 'string' ? (
      <span className="text-emerald-700 dark:text-emerald-400 break-all">
        {highlight(`"${truncate(value, 180)}"`, q)}
      </span>
    ) : typeof value === 'number' ? (
      <span className="text-blue-700 dark:text-blue-400">{highlight(String(value), q)}</span>
    ) : typeof value === 'boolean' ? (
      <span className="text-purple-700 dark:text-purple-400">{highlight(String(value), q)}</span>
    ) : (
      <span className="text-slate-700 dark:text-slate-200">{String(value)}</span>
    );

  return (
    <div
      ref={(el) => {
        nodeRefs.current.set(path, el);
      }}
      className={clsx(
        'group rounded-lg',
        isSelected && 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-200 dark:ring-primary-800'
      )}
    >
      <div
        className={clsx(
          'flex items-start gap-2 py-1.5 px-2 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded-lg cursor-pointer'
        )}
        style={indentStyle}
        onClick={() => {
          onSelectPath(path);
          if (isContainer) toggle();
        }}
      >
        <div className="w-4 flex-shrink-0 pt-0.5">
          {isContainer ? (
            <ChevronRightIcon className={clsx('w-4 h-4 text-slate-400 transition-transform', isOpen && 'rotate-90')} />
          ) : (
            <span className="text-slate-300 dark:text-slate-700">•</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {highlight(name, q)}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{typeLabel}</span>
            {!isContainer && <span className="text-sm">{leaf}</span>}
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {typeof value === 'string' && value.length > 180 && (
            <button
              type="button"
              className="px-2 py-1 rounded-md text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              onClick={(e) => {
                e.stopPropagation();
                onViewString(path || name, value);
              }}
            >
              View
            </button>
          )}
          <button
            type="button"
            className="px-2 py-1 rounded-md text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            onClick={(e) => {
              e.stopPropagation();
              void copyPath();
            }}
            title="Copy path"
          >
            path
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded-md text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            onClick={(e) => {
              e.stopPropagation();
              void copyValue();
            }}
            title="Copy value"
          >
            val
          </button>
        </div>
      </div>

      {isContainer && isOpen && (
        <div className="mt-0.5">
          {isArray ? (
            (value as JsonValue[]).map((child, i) => (
              <JsonTreeNode
                key={`${path}[${i}]`}
                name={String(i)}
                value={child}
                path={pathJoin(path, String(i), true)}
                depth={depth + 1}
                expanded={expanded}
                setExpanded={setExpanded}
                q={q}
                selectedPath={selectedPath}
                onSelectPath={onSelectPath}
                nodeRefs={nodeRefs}
                onViewString={onViewString}
              />
            ))
          ) : (
            Object.entries(value as Record<string, JsonValue>).map(([k, child]) => (
              <JsonTreeNode
                key={`${path}.${k}`}
                name={k}
                value={child}
                path={pathJoin(path, k, false)}
                depth={depth + 1}
                expanded={expanded}
                setExpanded={setExpanded}
                q={q}
                selectedPath={selectedPath}
                onSelectPath={onSelectPath}
                nodeRefs={nodeRefs}
                onViewString={onViewString}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function JsonExplorer({
  value,
  className,
  defaultExpandDepth = 2,
}: {
  value: unknown;
  className?: string;
  defaultExpandDepth?: number;
}) {
  const normalized = useMemo(() => safeJsonify(value), [value]);
  const flat = useMemo(() => flattenJson(normalized), [normalized]);

  const [q, setQ] = useState('');
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Expand root and a couple levels by default
    const next = new Set<string>(['']);
    // We’ll refine below on first render.
    return next;
  });

  const [stringModal, setStringModal] = useState<{ title: string; value: string } | null>(null);

  const nodeRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Initialize expanded set to default depth once we have flattened paths
  useEffect(() => {
    const next = new Set<string>();
    next.add('');
    for (const e of flat) {
      // crude depth estimate: count '.' + '['
      const depth = (e.path.match(/[.[\]]/g) || []).filter((c) => c === '.' || c === '[').length;
      if (depth < defaultExpandDepth) {
        jsonParents(e.path).forEach((p) => next.add(p));
      }
    }
    setExpanded(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultExpandDepth, normalized]);

  const matches = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];

    return flat
      .filter((e) => {
        const keyHit = e.key.toLowerCase().includes(query);
        if (keyHit) return true;
        // Only search leaf previews + strings/numbers/bools
        if (typeof e.value === 'string') return e.value.toLowerCase().includes(query);
        if (typeof e.value === 'number' || typeof e.value === 'boolean') return String(e.value).toLowerCase().includes(query);
        if (typeof e.preview === 'string' && e.preview) return e.preview.toLowerCase().includes(query);
        return false;
      })
      .slice(0, 200);
  }, [flat, q]);

  // When searching, auto-expand to show matches
  useEffect(() => {
    const query = q.trim();
    if (!query) return;
    const next = new Set(expanded);
    for (const m of matches) {
      jsonParents(m.path).forEach((p) => next.add(p));
    }
    setExpanded(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, matches.length]);

  const jumpTo = (path: string) => {
    setSelectedPath(path);
    const el = nodeRefs.current.get(path);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const expandAll = () => {
    const next = new Set<string>();
    flat.forEach((e) => next.add(e.path));
    next.add('');
    setExpanded(next);
  };

  const collapseAll = () => {
    setExpanded(new Set(['']));
  };

  const copyAllJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(normalized, null, 2));
    } catch {
      // ignore
    }
  };

  return (
    <div className={clsx('rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900', className)}>
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">JSON Explorer</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={collapseAll}>
              Collapse
            </Button>
            <Button variant="outline" size="sm" onClick={expandAll}>
              Expand
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void copyAllJson()}
              leftIcon={<ClipboardDocumentIcon className="w-4 h-4" />}
            >
              Copy all
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              label=""
              placeholder="Search keys & values (e.g. markdown, page_index, Horla)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10"
            />
          </div>
          {q && (
            <button
              type="button"
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
              onClick={() => setQ('')}
              title="Clear search"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        {/* Results */}
        <div className="border-b lg:border-b-0 lg:border-r border-slate-100 dark:border-slate-800">
          <div className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
            Results {q ? `(${matches.length}${matches.length === 200 ? '+' : ''})` : ''}
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {!q ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                Type to search. Click a result to jump + auto-expand the matching branch.
              </div>
            ) : matches.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">No matches.</div>
            ) : (
              matches.map((m) => (
                <button
                  key={`res-${m.path}`}
                  type="button"
                  onClick={() => jumpTo(m.path)}
                  className={clsx(
                    'w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800',
                    selectedPath === m.path && 'bg-primary-50 dark:bg-primary-900/20'
                  )}
                >
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
                    {highlight(m.path || '(root)', q)}
                  </div>
                  <div className="text-sm text-slate-800 dark:text-slate-100 mt-1 break-words">
                    <span className="font-medium">{highlight(m.key, q)}</span>{' '}
                    <span className="text-xs text-slate-500 dark:text-slate-400">({m.type})</span>
                  </div>
                  {m.preview && (
                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 break-words">
                      {highlight(m.preview, q) as any}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Tree */}
        <div className="lg:col-span-2">
          <div className="max-h-[580px] overflow-y-auto p-3">
            <JsonTreeNode
              name="(root)"
              value={normalized}
              path=""
              depth={0}
              expanded={expanded}
              setExpanded={setExpanded}
              q={q.trim()}
              selectedPath={selectedPath}
              onSelectPath={(p) => setSelectedPath(p)}
              nodeRefs={nodeRefs}
              onViewString={(title, v) => setStringModal({ title, value: v })}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!stringModal}
        onClose={() => setStringModal(null)}
        title={stringModal?.title || 'Value'}
        description="Full string value"
        size="xl"
      >
        <ModalBody>
          <pre className="whitespace-pre-wrap break-words text-sm font-mono text-slate-800 dark:text-slate-100">
            {stringModal?.value}
          </pre>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (!stringModal) return;
              void navigator.clipboard.writeText(stringModal.value);
            }}
          >
            Copy
          </Button>
          <Button onClick={() => setStringModal(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}





